import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { flowRight, noop } from 'lodash';
import {
  SECTION_HOMEPAGE,
  SECTION_MY_POSTS,
  SECTION_POST_LIST,
  SECTION_RELATED_POSTS,
  type Section,
} from '@wix/communities-blog-client-common';
import { connect } from '../components/runtime-context';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { useIsFeedDesignEnabled } from '../hooks/use-is-feed-design-enabled';
import { getRoute } from '../router/router-selectors';
import {
  getIsCreatedWithResponsiveEditor,
  getIsFeedDesignPropsMigrated,
  getUseMobileDesignSettings,
} from '../selectors/app-settings-selectors';
import { shouldApplyPostDesignInFeed } from '../selectors/full-post-selectors';
import { getSection } from '../selectors/section-selectors';
import { isInRouteUsingFeedPage } from '../services/detect-route';
import { createFeedClassNameGenerator } from '../services/layout-config';
import { type AppState } from '../types';
import withHocName from './with-hoc-name';

export type WithIsFeedDesignEnabled = {
  isFeedDesignPropsMigrated: boolean;
  section: Section;
  isInFeed: boolean;
  isInSearch: boolean;
  isCreatedWithResponsiveEditor: boolean;
  useMobileSettings: boolean;
  shouldApplyPostDesignInFeed: boolean;
  getPostClassName: (...classNames: string[]) => string[];
  applyFeedDesign: boolean;
};

type WithIsFeedDesignEnabledOwnProps = {
  section?: Section;
};

/** @deprecated Use {@link useIsFeedDesignEnabled} hook instead */
const withIsFeedDesignEnabled = <P extends WithIsFeedDesignEnabled>(
  WrappedComponent: React.ComponentType<P>,
) => {
  const WithIsFeedDesignEnabledComponent = ({
    isCreatedWithResponsiveEditor,
    isFeedDesignPropsMigrated,
    isInFeed,
    isInSearch,
    section = SECTION_HOMEPAGE,
    useMobileSettings,
    shouldApplyPostDesignInFeed,
    ...props
  }: P) => {
    const shouldApplyDesignInFeed = isInFeed && !shouldApplyPostDesignInFeed;
    const applyFeedDesign =
      section === SECTION_POST_LIST ||
      section === SECTION_RELATED_POSTS ||
      section === SECTION_MY_POSTS ||
      ((isFeedDesignPropsMigrated || isCreatedWithResponsiveEditor) &&
        (shouldApplyDesignInFeed || isInSearch));
    const getPostClassName = applyFeedDesign
      ? createFeedClassNameGenerator(section, useMobileSettings)
      : noop;

    return (
      <WrappedComponent
        {...(props as P)}
        getPostClassName={getPostClassName}
        applyFeedDesign={applyFeedDesign}
        section={section}
      />
    );
  };

  hoistNonReactStatics(WithIsFeedDesignEnabledComponent, WrappedComponent);

  const mapRuntimeToProps = (state: AppState, { section }: WithIsFeedDesignEnabledOwnProps) => {
    const route = getRoute(state);
    return {
      section: getSection(state) || section,
      isInFeed: isInRouteUsingFeedPage(route),
      isInSearch: false, // TODO: isInSearchPage(route),
      isCreatedWithResponsiveEditor: getIsCreatedWithResponsiveEditor(state),
      isFeedDesignPropsMigrated: getIsFeedDesignPropsMigrated(state),
      useMobileSettings: getUseMobileDesignSettings(state),
      shouldApplyPostDesignInFeed: shouldApplyPostDesignInFeed(state),
    };
  };

  return flowRight(
    connect(mapRuntimeToProps),
    withHocName('WithIsFeedDesignEnabled'),
  )(WithIsFeedDesignEnabledComponent);
};

export default withIsFeedDesignEnabled;
